import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import AdminBlog from './AdminBlog';
import VerificationAdmin from './VerificationAdmin';

const AdminPanel = ({ user, adminUser, adminLevel: passedAdminLevel }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentAdminLevel, setCurrentAdminLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddAdForm, setShowAddAdForm] = useState(false);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  // Ad management states
  const [adTitle, setAdTitle] = useState('');
  const [adContent, setAdContent] = useState('');
  
  // Users management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Subscriptions management
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  // Analytics data
  const [analytics, setAnalytics] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Coins management states
  const [coinUsers, setCoinUsers] = useState([]);
  const [coinUsersLoading, setCoinUsersLoading] = useState(false);
  const [coinStats, setCoinStats] = useState({});
  const [coinStatsLoading, setCoinStatsLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [coinAdjustmentAmount, setCoinAdjustmentAmount] = useState('');
  const [coinAdjustmentReason, setCoinAdjustmentReason] = useState('');
  const [coinHistory, setCoinHistory] = useState([]);
  const [coinHistoryLoading, setCoinHistoryLoading] = useState(false);
  const [showCoinHistory, setShowCoinHistory] = useState(false);

  // Mandatory ads states
  const [mandatoryAds, setMandatoryAds] = useState([]);
  const [mandatoryAdsLoading, setMandatoryAdsLoading] = useState(false);
  const [showAddMandatoryAdForm, setShowAddMandatoryAdForm] = useState(false);
  const [mandatoryAdType, setMandatoryAdType] = useState('video');
  const [mandatoryAdTitle, setMandatoryAdTitle] = useState('');
  const [mandatoryAdContent, setMandatoryAdContent] = useState('');
  const [mandatoryAdDescription, setMandatoryAdDescription] = useState('');
  const [mandatoryAdDuration, setMandatoryAdDuration] = useState(10);

  // Ad settings states
  const [adSettings, setAdSettings] = useState({});
  const [videoPercentage, setVideoPercentage] = useState(70);
  const [websitePercentage, setWebsitePercentage] = useState(30);
  const [dailyLimit, setDailyLimit] = useState(3);

  useEffect(() => {
    checkAdminAccess();
  }, [user, adminUser, passedAdminLevel]);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'analytics') {
        fetchAnalytics();
      } else if (activeTab === 'subscriptions') {
        fetchSubscriptions();
      } else if (activeTab === 'ads') {
        fetchAds();
        fetchMandatoryAds();
        fetchAdSettings();
      } else if (activeTab === 'reports') {
        fetchReports();
      } else if (activeTab === 'coins') {
        fetchCoinUsers();
        fetchCoinStats();
      }
    }
  }, [activeTab, isAdmin]);

  const checkAdminAccess = async () => {
    try {
      if (adminUser && passedAdminLevel) {
        setIsAdmin(true);
        setCurrentAdminLevel(passedAdminLevel);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    console.log('Starting fetchUsers...');
    try {
      // First try to get profiles without subscriptions
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Profiles query result:', profiles);
      console.log('Profiles query error:', profilesError);

      if (profilesError) throw profilesError;

      // Get subscriptions directly with updated RLS policies
      let subscriptions = [];
      try {
        const { data: subs, error: subsError } = await supabase
          .from('user_subscriptions')
          .select('user_id, billing_cycle, status, expires_at, is_premium');
        
        if (!subsError && subs) {
          subscriptions = subs;
          console.log('Subscriptions loaded directly:', subscriptions);
          console.log('Sample subscription data:', subscriptions[0]);
        } else {
          console.log('Direct subscription query error:', subsError);
        }
      } catch (subError) {
        console.log('Subscription query failed:', subError);
      }

      // Format users for display
      const combinedUsers = profiles.map(profile => {
        // Find subscription for this user
        const subscription = subscriptions.find(sub => sub.user_id === profile.id);
        const isPremium = subscription && (
          // Check is_premium field directly (new way)
          subscription.is_premium === true ||
          // Fallback to old logic for backwards compatibility
          (subscription.status === 'active' && 
           new Date(subscription.expires_at) > new Date())
        );
        
        // Debug logging for the first few users
        if (profiles.indexOf(profile) < 3) {
          console.log(`User ${profile.id}:`, {
            hasSubscription: !!subscription,
            subscription: subscription,
            isPremiumField: subscription?.is_premium,
            isActive: subscription?.status === 'active',
            expiresAt: subscription?.expires_at,
            isNotExpired: subscription?.expires_at ? new Date(subscription.expires_at) > new Date() : false,
            finalIsPremium: isPremium
          });
        }
        
        return {
          ...profile,
          email: profile.email || `User ID: ${profile.id.substring(0, 8)}...`,
          email_confirmed: true,
          last_sign_in: profile.updated_at,
          created_at: profile.created_at,
          subscription: subscription ? {
            ...subscription,
            is_premium: isPremium
          } : null
        };
      });

      console.log('Combined users:', combinedUsers);
      setUsers(combinedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          profiles(name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const fetchReports = async () => {
  setReportsLoading(true);
  try {
    const { data, error } = await supabase
      .from('message_reports')
      .select(`
        *,
        reporter:profiles!reporter_id(name, email),
        reported_user:profiles!reported_user_id(name, email)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get message content for each report
    const reportsWithMessages = await Promise.all((data || []).map(async (report) => {
      try {
        const { data: messageData } = await supabase
          .from('messages')
          .select('content')
          .eq('id', report.message_id)
          .single();
        
        return {
          ...report,
          message_content: messageData?.content || 'Message not found'
        };
      } catch (error) {
        return {
          ...report,
          message_content: 'Message not found'
        };
      }
    }));

    setReports(reportsWithMessages);
  } catch (error) {
    console.error('Error fetching reports:', error);
  } finally {
    setReportsLoading(false);
  }
};

const fetchAnalytics = async () => {
  setAnalyticsLoading(true);
  try {
    // Get all profiles for analytics
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('gender, sexual_orientation, looking_for, age, created_at');

    if (profilesError) throw profilesError;

    // Get total matches for success rate
    const { data: matches, error: matchesError } = await supabase
      .from('matches')
      .select('*');

    if (matchesError) throw matchesError;

    // Calculate gender distribution
    const genderStats = profiles.reduce((acc, profile) => {
      const gender = profile.gender || 'unspecified';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {});

    // Calculate sexual orientation distribution
    const orientationStats = profiles.reduce((acc, profile) => {
      const orientation = profile.sexual_orientation || 'unspecified';
      acc[orientation] = (acc[orientation] || 0) + 1;
      return acc;
    }, {});

    // Calculate looking for preferences
    const lookingForStats = profiles.reduce((acc, profile) => {
      const lookingFor = profile.looking_for || 'unspecified';
      acc[lookingFor] = (acc[lookingFor] || 0) + 1;
      return acc;
    }, {});

    // Calculate age distribution
    const ageGroups = profiles.reduce((acc, profile) => {
      const age = parseInt(profile.age);
      if (age >= 18 && age <= 24) acc['18-24'] = (acc['18-24'] || 0) + 1;
      else if (age >= 25 && age <= 34) acc['25-34'] = (acc['25-34'] || 0) + 1;
      else if (age >= 35 && age <= 44) acc['35-44'] = (acc['35-44'] || 0) + 1;
      else if (age >= 45) acc['45+'] = (acc['45+'] || 0) + 1;
      return acc;
    }, {});

    // Calculate registration trends (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentUsers = profiles.filter(profile => 
      new Date(profile.created_at) >= thirtyDaysAgo
    ).length;

    setAnalytics({
      totalUsers: profiles.length,
      recentUsers,
      totalMatches: matches.length,
      genderStats,
      orientationStats,
      lookingForStats,
      ageGroups
    });

  } catch (error) {
    console.error('Error fetching analytics:', error);
  } finally {
    setAnalyticsLoading(false);
  }
};

  const resolveReport = async (reportId) => {
    try {
      const { error } = await supabase
        .from('message_reports')
        .update({ status: 'resolved' })
        .eq('id', reportId);

      if (error) throw error;
      
      alert('Report marked as resolved');
      fetchReports(); // Refresh the list
    } catch (error) {
      console.error('Error resolving report:', error);
      alert('Failed to resolve report');
    }
  };

  const toggleUserBlock = async (userId, currentBlockStatus) => {
    const action = currentBlockStatus ? 'unblock' : 'block';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      const updateData = currentBlockStatus 
        ? { blocked: false, blocked_at: null }
        : { blocked: true, blocked_at: new Date().toISOString() };

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) throw error;
      
      alert(`User ${action}ed successfully`);
      fetchUsers();
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      alert(`Failed to ${action} user`);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;
    if (currentAdminLevel !== 'super_admin') {
      alert('Only super admins can delete users');
      return;
    }

    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (profileError) throw profileError;

      alert('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const changeSubscription = async (userId, newTier) => {
    if (!window.confirm(`Are you sure you want to change this user's subscription to ${newTier.toUpperCase()}?`)) {
      return;
    }

    try {
      console.log(`Changing subscription for user ${userId} to ${newTier}`);
      
      if (newTier === 'premium') {
        const expiresAt = new Date();
        expiresAt.setMonth(expiresAt.getMonth() + 1);
        
        // Use the admin function to bypass RLS
        const { error } = await supabase
          .rpc('admin_update_subscription', {
            target_user_id: userId,
            new_status: 'active',
            new_is_premium: true,
            new_billing_cycle: 'monthly',
            new_expires_at: expiresAt.toISOString()
          });
          
        if (error) throw error;

        // Add premium coins to user
        try {
          const { error: coinsError } = await supabase
            .rpc('add_coins', {
              user_uuid: userId,
              amount: 500
            });

          if (coinsError) {
            console.log('Note: Could not add coins automatically, but subscription was upgraded.');
          }
        } catch (coinError) {
          console.log('Note: Subscription upgraded but coins could not be added.');
        }

      } else {
        // Set to free - cancel subscription
        const { error } = await supabase
          .rpc('admin_update_subscription', {
            target_user_id: userId,
            new_status: 'cancelled',
            new_is_premium: false,
            new_billing_cycle: 'monthly',
            new_expires_at: new Date().toISOString()
          });
          
        if (error) throw error;
      }

      console.log('Subscription change successful');
      alert(`User subscription successfully changed to ${newTier.toUpperCase()}!`);
      
      // Refresh both user and subscription lists
      await Promise.all([fetchUsers(), fetchSubscriptions()]);
      
    } catch (error) {
      console.error('Error changing subscription:', error);
      alert(`Failed to change subscription: ${error.message}`);
    }
  };

  const saveAd = async () => {
    if (!adTitle.trim() || !adContent.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    try {
      const { error } = await supabase
        .from('ads')
        .insert({
          title: adTitle,
          content: adContent,
          is_active: true
        });

      if (error) throw error;

      alert('Ad created successfully!');
      setAdTitle('');
      setAdContent('');
      setShowAddAdForm(false);
      fetchAds();
    } catch (error) {
      console.error('Error saving ad:', error);
      alert('Failed to save ad');
    }
  };

  const fetchAds = async () => {
    setAdsLoading(true);
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAds(data);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setAdsLoading(false);
    }
  };

  const toggleAdStatus = async (adId, newStatus) => {
    try {
      const { error } = await supabase
        .from('ads')
        .update({ is_active: newStatus })
        .eq('id', adId);

      if (error) throw error;
      
      alert(`Ad ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchAds(); // Refresh the list
    } catch (error) {
      console.error('Error updating ad status:', error);
      alert('Failed to update ad status');
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    
    try {
      const { error } = await supabase
        .from('ads')
        .delete()
        .eq('id', adId);

      if (error) throw error;
      
      alert('Ad deleted successfully!');
      fetchAds(); // Refresh the list
    } catch (error) {
      console.error('Error deleting ad:', error);
      alert('Failed to delete ad');
    }
  };

  const fetchMandatoryAds = async () => {
    setMandatoryAdsLoading(true);
    try {
      const { data, error } = await supabase
        .from('mandatory_ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMandatoryAds(data);
    } catch (error) {
      console.error('Error fetching mandatory ads:', error);
    } finally {
      setMandatoryAdsLoading(false);
    }
  };

  const fetchAdSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('ad_settings')
        .select('*');

      if (error) throw error;
      
      const settings = {};
      data.forEach(setting => {
        settings[setting.setting_name] = setting.setting_value;
      });
      
      setAdSettings(settings);
      setVideoPercentage(parseInt(settings.video_percentage) || 70);
      setWebsitePercentage(parseInt(settings.website_percentage) || 30);
      setDailyLimit(parseInt(settings.daily_mandatory_limit) || 3);
    } catch (error) {
      console.error('Error fetching ad settings:', error);
    }
  };

  const saveMandatoryAd = async () => {
    if (!mandatoryAdTitle.trim() || !mandatoryAdContent.trim()) {
      alert('Please fill in title and content');
      return;
    }

    try {
      const { error } = await supabase
        .from('mandatory_ads')
        .insert({
          title: mandatoryAdTitle,
          type: mandatoryAdType,
          content: mandatoryAdContent,
          description: mandatoryAdDescription,
          duration: mandatoryAdType === 'video' ? mandatoryAdDuration : null,
          is_active: true
        });

      if (error) throw error;

      alert('Mandatory ad created successfully!');
      setMandatoryAdTitle('');
      setMandatoryAdContent('');
      setMandatoryAdDescription('');
      setMandatoryAdDuration(10);
      setShowAddMandatoryAdForm(false);
      fetchMandatoryAds();
    } catch (error) {
      console.error('Error saving mandatory ad:', error);
      alert('Failed to save mandatory ad');
    }
  };

  const toggleMandatoryAdStatus = async (adId, newStatus) => {
    try {
      const { error } = await supabase
        .from('mandatory_ads')
        .update({ is_active: newStatus })
        .eq('id', adId);

      if (error) throw error;
      
      alert(`Mandatory ad ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchMandatoryAds();
    } catch (error) {
      console.error('Error updating mandatory ad status:', error);
      alert('Failed to update mandatory ad status');
    }
  };

  const deleteMandatoryAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this mandatory ad?')) return;
    
    try {
      const { error } = await supabase
        .from('mandatory_ads')
        .delete()
        .eq('id', adId);

      if (error) throw error;
      
      alert('Mandatory ad deleted successfully!');
      fetchMandatoryAds();
    } catch (error) {
      console.error('Error deleting mandatory ad:', error);
      alert('Failed to delete mandatory ad');
    }
  };

  const updateAdSettings = async () => {
    try {
      const updates = [
        { setting_name: 'video_percentage', setting_value: videoPercentage.toString() },
        { setting_name: 'website_percentage', setting_value: websitePercentage.toString() },
        { setting_name: 'daily_mandatory_limit', setting_value: dailyLimit.toString() }
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('ad_settings')
          .upsert(update, { onConflict: 'setting_name' });
        
        if (error) throw error;
      }

      alert('Ad settings updated successfully!');
      fetchAdSettings();
    } catch (error) {
      console.error('Error updating ad settings:', error);
      alert('Failed to update ad settings');
    }
  };

  // Coin management functions
  const fetchCoinUsers = async () => {
    setCoinUsersLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_premium_users_view')
        .select('*')
        .order('current_balance', { ascending: false });

      if (error) throw error;
      setCoinUsers(data || []);
    } catch (error) {
      console.error('Error fetching coin users:', error);
    } finally {
      setCoinUsersLoading(false);
    }
  };

  const fetchCoinStats = async () => {
    setCoinStatsLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('admin_get_coin_stats', { p_admin_id: user.id });

      if (error) throw error;
      setCoinStats(data && data.length > 0 ? data[0] : {});
    } catch (error) {
      console.error('Error fetching coin stats:', error);
    } finally {
      setCoinStatsLoading(false);
    }
  };

  const adjustUserCoins = async () => {
    if (!selectedUserId || !coinAdjustmentAmount) {
      alert('Please select a user and enter an amount');
      return;
    }

    const amount = parseInt(coinAdjustmentAmount);
    if (isNaN(amount)) {
      alert('Please enter a valid number');
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('admin_adjust_user_coins', {
          p_admin_id: user.id,
          p_target_user_id: selectedUserId,
          p_adjustment_amount: amount,
          p_reason: coinAdjustmentReason || 'Admin adjustment'
        });

      if (error) throw error;

      if (data && data.length > 0) {
        const result = data[0];
        if (result.success) {
          alert(`Coins adjusted successfully! ${result.old_balance} → ${result.new_balance}`);
          setCoinAdjustmentAmount('');
          setCoinAdjustmentReason('');
          setSelectedUserId('');
          fetchCoinUsers(); // Refresh the list
        } else {
          alert(`Failed to adjust coins: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error adjusting coins:', error);
      alert('Failed to adjust coins: ' + error.message);
    }
  };

  const fetchUserCoinHistory = async (userId, userName) => {
    setCoinHistoryLoading(true);
    setShowCoinHistory(true);
    try {
      const { data, error } = await supabase
        .rpc('admin_get_user_coin_history', {
          p_admin_id: user.id,
          p_target_user_id: userId,
          p_limit: 50
        });

      if (error) throw error;
      setCoinHistory(data || []);
    } catch (error) {
      console.error('Error fetching coin history:', error);
      setCoinHistory([]);
    } finally {
      setCoinHistoryLoading(false);
    }
  };

  const filteredUsers = users.filter(userItem => 
    userItem.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    userItem.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ 
        padding: '40px',
        textAlign: 'center',
        color: 'white'
      }}>
        Checking admin access...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ color: '#ff6b6b', marginBottom: '15px' }}>Access Denied</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            You don't have admin permissions to access this panel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '10px'
          }}>
            Admin Panel
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Level: <span style={{ color: '#00E9FF', fontWeight: '600' }}>{currentAdminLevel}</span>
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          justifyContent: 'center'
        }}>
          {['users', 'analytics', 'subscriptions', 'coins', 'ads', 'blog', 'verification', 'reports'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: 'none',
                background: activeTab === tab 
                  ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                  : 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab === 'users' ? '👥 Users' : 
               tab === 'analytics' ? '📊 Analytics' :
               tab === 'subscriptions' ? '💎 Subscriptions' :
               tab === 'coins' ? '🪙 Coins' : 
               tab === 'ads' ? '📢 Ads' :
               tab === 'blog' ? '📝 Blog' :
               tab === 'verification' ? '✅ Verification' :
               '⚠️ Reports'}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#00E9FF', margin: 0 }}>User Management</h3>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>

              {usersLoading ? (
                <p style={{ color: 'white', textAlign: 'center' }}>Loading users...</p>
              ) : filteredUsers.length === 0 ? (
                <p style={{ color: 'white', textAlign: 'center' }}>No users found.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Subscription</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(userItem => (
                        <tr key={userItem.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <td style={{ padding: '12px', color: 'white' }}>
                            {userItem.name || 'No name'}
                            {userItem.age && <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}> ({userItem.age})</span>}
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            {userItem.email}
                            {!userItem.email_confirmed && 
                              <span style={{ color: '#ff6b6b', fontSize: '12px' }}> (unverified)</span>
                            }
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: userItem.subscription?.is_premium 
                                ? 'rgba(0, 233, 255, 0.2)' 
                                : 'rgba(255, 255, 255, 0.1)',
                              color: userItem.subscription?.is_premium ? '#00E9FF' : 'rgba(255, 255, 255, 0.7)'
                            }}>
                              {userItem.subscription?.is_premium ? 'Premium' : 'Free'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: userItem.blocked ? 'rgba(255, 107, 107, 0.2)' : 'rgba(81, 207, 102, 0.2)',
                              color: userItem.blocked ? '#ff6b6b' : '#51cf66'
                            }}>
                              {userItem.blocked ? 'Blocked' : 'Active'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    changeSubscription(userItem.id, e.target.value);
                                    e.target.value = ''; // Reset dropdown after selection
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  fontSize: '12px'
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>
                                  {userItem.subscription?.is_premium ? 'Premium → Free' : 'Free → Premium'}
                                </option>
                                {!userItem.subscription?.is_premium && <option value="premium">Upgrade to Premium</option>}
                                {userItem.subscription?.is_premium && <option value="free">Downgrade to Free</option>}
                              </select>
                              <button
                                onClick={() => toggleUserBlock(userItem.id, userItem.blocked)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: userItem.blocked 
                                    ? 'rgba(81, 207, 102, 0.2)' 
                                    : 'rgba(255, 107, 107, 0.2)',
                                  color: userItem.blocked ? '#51cf66' : '#ff6b6b',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                {userItem.blocked ? 'Unblock' : 'Block'}
                              </button>
                              {currentAdminLevel === 'super_admin' && (
                                <button
                                  onClick={() => deleteUser(userItem.id)}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: 'rgba(255, 0, 0, 0.3)',
                                    color: '#ff0000',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>Subscription Management</h3>
              
              {subscriptionsLoading ? (
                <p style={{ color: 'white', textAlign: 'center' }}>Loading subscriptions...</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>User</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Plan</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Billing</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map(sub => (
                        <tr key={sub.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <td style={{ padding: '12px', color: 'white' }}>
                            {sub.profiles?.name || 'No name'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: (sub.status === 'active' && new Date(sub.expires_at) > new Date())
                                ? 'rgba(0, 233, 255, 0.2)' 
                                : 'rgba(255, 255, 255, 0.1)',
                              color: (sub.status === 'active' && new Date(sub.expires_at) > new Date()) ? '#00E9FF' : 'rgba(255, 255, 255, 0.7)'
                            }}>
                              {(sub.status === 'active' && new Date(sub.expires_at) > new Date()) ? 'Premium' : 'Free'}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            {sub.billing_cycle || 'N/A'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: sub.status === 'active' 
                                ? 'rgba(81, 207, 102, 0.2)' 
                                : 'rgba(255, 107, 107, 0.2)',
                              color: sub.status === 'active' ? '#51cf66' : '#ff6b6b'
                            }}>
                              {sub.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coins Tab */}
        {activeTab === 'coins' && (
          <div>
            {/* Coin Statistics */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>🪙 Coin System Statistics</h3>
              
              {coinStatsLoading ? (
                <p style={{ color: 'white', textAlign: 'center' }}>Loading statistics...</p>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '15px' 
                }}>
                  <div style={{
                    background: 'rgba(0, 233, 255, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: '#00E9FF', margin: '0 0 10px 0' }}>Total Users</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {coinStats.total_users || 0}
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(138, 43, 226, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: '#8A2BE2', margin: '0 0 10px 0' }}>Premium Users</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {coinStats.premium_users || 0}
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(255, 193, 7, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: '#ffc107', margin: '0 0 10px 0' }}>Total Coins</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {coinStats.total_coins_in_system || 0}
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(81, 207, 102, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: '#51cf66', margin: '0 0 10px 0' }}>Coins Earned</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {coinStats.total_coins_earned || 0}
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: '#ff6b6b', margin: '0 0 10px 0' }}>Coins Spent</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {coinStats.total_coins_spent || 0}
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '10px',
                    padding: '15px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>Avg Balance</h4>
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {parseFloat(coinStats.avg_balance_per_user || 0).toFixed(1)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Coin Adjustment Tool */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>🔧 Adjust User Coins</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '15px', alignItems: 'end' }}>
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Select User</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  >
                    <option value="" style={{ background: '#1a1a2e' }}>Select a user...</option>
                    {coinUsers.map(user => (
                      <option key={user.user_id} value={user.user_id} style={{ background: '#1a1a2e' }}>
                        {user.name || user.email} - {user.current_balance || 0} coins
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Amount (+/-)</label>
                  <input
                    type="number"
                    placeholder="e.g. 100 or -50"
                    value={coinAdjustmentAmount}
                    onChange={(e) => setCoinAdjustmentAmount(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Reason (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Bonus, Refund, Penalty"
                    value={coinAdjustmentReason}
                    onChange={(e) => setCoinAdjustmentReason(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  />
                </div>
                
                <button
                  onClick={adjustUserCoins}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                    color: 'white',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Adjust Coins
                </button>
              </div>
            </div>

            {/* Users with Coins */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>👥 Users & Coin Balances</h3>
              
              {coinUsersLoading ? (
                <p style={{ color: 'white', textAlign: 'center' }}>Loading users...</p>
              ) : coinUsers.length === 0 ? (
                <p style={{ color: 'white', textAlign: 'center' }}>No users found.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>User</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Premium</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Balance</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Earned</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Spent</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Withdrawn</th>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#00E9FF' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coinUsers.map(user => (
                        <tr key={user.user_id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <td style={{ padding: '12px', color: 'white' }}>
                            <div>
                              <div>{user.name || 'No name'}</div>
                              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                {user.email}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: user.premium_status === 'Active Premium' 
                                ? 'rgba(0, 233, 255, 0.2)' 
                                : 'rgba(255, 255, 255, 0.1)',
                              color: user.premium_status === 'Active Premium' ? '#00E9FF' : 'rgba(255, 255, 255, 0.7)'
                            }}>
                              {user.premium_status || 'Free'}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: '#00E9FF', fontWeight: 'bold' }}>
                            {user.current_balance || 0}
                          </td>
                          <td style={{ padding: '12px', color: '#51cf66' }}>
                            {user.total_earned || 0}
                          </td>
                          <td style={{ padding: '12px', color: '#ff6b6b' }}>
                            {user.total_spent || 0}
                          </td>
                          <td style={{ padding: '12px', color: '#ffc107' }}>
                            {user.withdrawn_coins || 0}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <button
                              onClick={() => fetchUserCoinHistory(user.user_id, user.name || user.email)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'rgba(138, 43, 226, 0.2)',
                                color: '#8A2BE2',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              History
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Coin History Modal */}
            {showCoinHistory && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  borderRadius: '20px',
                  padding: '30px',
                  maxWidth: '800px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  border: '2px solid #00E9FF',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
                }}>
                  <h3 style={{ 
                    color: '#00E9FF', 
                    marginBottom: '20px',
                    fontSize: '20px',
                    textAlign: 'center'
                  }}>
                    🪙 Coin Transaction History
                  </h3>
                  
                  {coinHistoryLoading ? (
                    <p style={{ color: 'white', textAlign: 'center' }}>Loading history...</p>
                  ) : coinHistory.length === 0 ? (
                    <p style={{ color: 'white', textAlign: 'center' }}>No transactions found.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '13px'
                      }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#00E9FF' }}>Date</th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#00E9FF' }}>Type</th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#00E9FF' }}>Amount</th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#00E9FF' }}>Description</th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#00E9FF' }}>Admin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {coinHistory.map((transaction, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                              <td style={{ padding: '10px', color: 'rgba(255, 255, 255, 0.8)' }}>
                                {new Date(transaction.transaction_date).toLocaleDateString()}
                              </td>
                              <td style={{ padding: '10px', color: 'white' }}>
                                {transaction.transaction_type.replace('_', ' ')}
                              </td>
                              <td style={{ 
                                padding: '10px', 
                                color: transaction.amount > 0 ? '#51cf66' : '#ff6b6b',
                                fontWeight: 'bold'
                              }}>
                                {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                              </td>
                              <td style={{ padding: '10px', color: 'rgba(255, 255, 255, 0.8)' }}>
                                {transaction.description || 'No description'}
                              </td>
                              <td style={{ padding: '10px', color: 'rgba(255, 255, 255, 0.8)' }}>
                                {transaction.admin_name || 'System'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setShowCoinHistory(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      marginTop: '20px',
                      width: '100%'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ads Tab */}
        {activeTab === 'ads' && (
          <div>
            {/* Ad Settings Panel */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>Ad Settings</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Video Ads %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={videoPercentage}
                    onChange={(e) => setVideoPercentage(parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Website Ads %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={websitePercentage}
                    onChange={(e) => setWebsitePercentage(parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Daily Mandatory Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={dailyLimit}
                    onChange={(e) => setDailyLimit(parseInt(e.target.value) || 1)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                  />
                </div>
              </div>
              <button
                onClick={updateAdSettings}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Update Settings
              </button>
            </div>

            {/* Banner Ads Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>Banner Ads (Every 2 likes)</h3>
              
              <button
                onClick={() => setShowAddAdForm(!showAddAdForm)}
                style={{
                  backgroundColor: '#00E9FF',
                  color: '#000',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginBottom: '20px'
                }}
              >
                {showAddAdForm ? 'Cancel' : 'Add Banner Ad'}
              </button>

              {/* Add New Banner Ad Form */}
              {showAddAdForm && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: '#00E9FF', marginBottom: '15px' }}>Create Banner Ad</h4>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    <input
                      type="text"
                      placeholder="Ad Title"
                      value={adTitle}
                      onChange={(e) => setAdTitle(e.target.value)}
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      }}
                    />
                    <textarea
                      placeholder="Ad Content/Description"
                      value={adContent}
                      onChange={(e) => setAdContent(e.target.value)}
                      rows="3"
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        resize: 'vertical'
                      }}
                    />
                    <button
                      onClick={saveAd}
                      style={{
                        backgroundColor: '#00E9FF',
                        color: '#000',
                        border: 'none',
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Create Banner Ad
                    </button>
                  </div>
                </div>
              )}

              {/* Banner Ads List */}
              <div>
                <h4 style={{ color: '#00E9FF', marginBottom: '15px' }}>Banner Ads</h4>
                {adsLoading ? (
                  <p style={{ color: 'white' }}>Loading banner ads...</p>
                ) : ads.length === 0 ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>No banner ads created yet.</p>
                ) : (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {ads.map(ad => (
                      <div key={ad.id} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <h5 style={{ color: '#00E9FF', margin: '0 0 8px 0' }}>{ad.title}</h5>
                        <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '0 0 8px 0' }}>{ad.content}</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background: ad.is_active ? 'rgba(81, 207, 102, 0.2)' : 'rgba(255, 107, 107, 0.2)',
                            color: ad.is_active ? '#51cf66' : '#ff6b6b'
                          }}>
                            {ad.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => toggleAdStatus(ad.id, !ad.is_active)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: ad.is_active ? 'rgba(255, 193, 7, 0.2)' : 'rgba(40, 167, 69, 0.2)',
                                color: ad.is_active ? '#ffc107' : '#28a745',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              {ad.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => deleteAd(ad.id)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'rgba(220, 53, 69, 0.2)',
                                color: '#dc3545',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mandatory Ads Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px'
            }}>
              <h3 style={{ color: '#00E9FF', marginBottom: '20px' }}>Mandatory Ads (Video & Website)</h3>
              
              <button
                onClick={() => setShowAddMandatoryAdForm(!showAddMandatoryAdForm)}
                style={{
                  backgroundColor: '#8A2BE2',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginBottom: '20px'
                }}
              >
                {showAddMandatoryAdForm ? 'Cancel' : 'Add Mandatory Ad'}
              </button>

              {/* Add New Mandatory Ad Form */}
              {showAddMandatoryAdForm && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: '#00E9FF', marginBottom: '15px' }}>Create Mandatory Ad</h4>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    <div>
                      <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Ad Type</label>
                      <select
                        value={mandatoryAdType}
                        onChange={(e) => setMandatoryAdType(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }}
                      >
                        <option value="video" style={{ background: '#1a1a2e' }}>Video Ad</option>
                        <option value="website" style={{ background: '#1a1a2e' }}>Website Link Ad</option>
                      </select>
                    </div>
                    <input
                      type="text"
                      placeholder="Ad Title"
                      value={mandatoryAdTitle}
                      onChange={(e) => setMandatoryAdTitle(e.target.value)}
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      }}
                    />
                    <input
                      type="text"
                      placeholder={mandatoryAdType === 'video' ? 'Video URL' : 'Website URL'}
                      value={mandatoryAdContent}
                      onChange={(e) => setMandatoryAdContent(e.target.value)}
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      }}
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={mandatoryAdDescription}
                      onChange={(e) => setMandatoryAdDescription(e.target.value)}
                      rows="2"
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        resize: 'vertical'
                      }}
                    />
                    {mandatoryAdType === 'video' && (
                      <div>
                        <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Duration (seconds)</label>
                        <input
                          type="number"
                          min="5"
                          max="30"
                          value={mandatoryAdDuration}
                          onChange={(e) => setMandatoryAdDuration(parseInt(e.target.value) || 10)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                        />
                      </div>
                    )}
                    <button
                      onClick={saveMandatoryAd}
                      style={{
                        backgroundColor: '#8A2BE2',
                        color: 'white',
                        border: 'none',
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Create Mandatory Ad
                    </button>
                  </div>
                </div>
              )}

              {/* Mandatory Ads List */}
              <div>
                <h4 style={{ color: '#00E9FF', marginBottom: '15px' }}>Mandatory Ads</h4>
                {mandatoryAdsLoading ? (
                  <p style={{ color: 'white' }}>Loading mandatory ads...</p>
                ) : mandatoryAds.length === 0 ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>No mandatory ads created yet.</p>
                ) : (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {mandatoryAds.map(ad => (
                      <div key={ad.id} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background: ad.type === 'video' ? 'rgba(220, 53, 69, 0.2)' : 'rgba(0, 123, 255, 0.2)',
                            color: ad.type === 'video' ? '#dc3545' : '#007bff'
                          }}>
                            {ad.type === 'video' ? '📹 VIDEO' : '🌐 WEBSITE'}
                          </span>
                          <h5 style={{ color: '#00E9FF', margin: 0 }}>{ad.title}</h5>
                        </div>
                        <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '0 0 8px 0', fontSize: '14px' }}>
                          {ad.type === 'video' ? `Video: ${ad.content}` : `Website: ${ad.content}`}
                        </p>
                        {ad.description && (
                          <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 8px 0', fontSize: '12px' }}>
                            {ad.description}
                          </p>
                        )}
                        {ad.type === 'video' && (
                          <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 8px 0', fontSize: '12px' }}>
                            Duration: {ad.duration} seconds
                          </p>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background: ad.is_active ? 'rgba(81, 207, 102, 0.2)' : 'rgba(255, 107, 107, 0.2)',
                            color: ad.is_active ? '#51cf66' : '#ff6b6b'
                          }}>
                            {ad.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => toggleMandatoryAdStatus(ad.id, !ad.is_active)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: ad.is_active ? 'rgba(255, 193, 7, 0.2)' : 'rgba(40, 167, 69, 0.2)',
                                color: ad.is_active ? '#ffc107' : '#28a745',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              {ad.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => deleteMandatoryAd(ad.id)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'rgba(220, 53, 69, 0.2)',
                                color: '#dc3545',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && <AdminBlog />}

        {/* Verification Tab */}
        {activeTab === 'verification' && <VerificationAdmin user={user} adminUser={adminUser} adminLevel={currentAdminLevel} />}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#00E9FF', margin: '0 0 20px 0' }}>📊 Platform Analytics</h3>
              
              {analyticsLoading ? (
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Loading analytics...</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  
                  {/* Overview Stats */}
                  <div style={{ background: 'rgba(0, 233, 255, 0.1)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: '#00E9FF', margin: '0 0 15px 0' }}>📈 Overview</h4>
                    <div style={{ color: 'white' }}>
                      <p>Total Users: <strong>{analytics.totalUsers || 0}</strong></p>
                      <p>New Users (30 days): <strong>{analytics.recentUsers || 0}</strong></p>
                      <p>Total Matches: <strong>{analytics.totalMatches || 0}</strong></p>
                      <p>Match Rate: <strong>{analytics.totalUsers > 0 ? ((analytics.totalMatches / analytics.totalUsers) * 100).toFixed(1) : 0}%</strong></p>
                    </div>
                  </div>

                  {/* Gender Distribution */}
                  <div style={{ background: 'rgba(138, 43, 226, 0.1)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: '#8A2BE2', margin: '0 0 15px 0' }}>⚧️ Gender Distribution</h4>
                    <div style={{ color: 'white' }}>
                      {analytics.genderStats && Object.entries(analytics.genderStats).map(([gender, count]) => (
                        <p key={gender}>
                          {gender.charAt(0).toUpperCase() + gender.slice(1)}: <strong>{count}</strong> 
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}> ({((count / analytics.totalUsers) * 100).toFixed(1)}%)</span>
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Sexual Orientation */}
                  <div style={{ background: 'rgba(255, 107, 107, 0.1)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: '#ff6b6b', margin: '0 0 15px 0' }}>🌈 Sexual Orientation</h4>
                    <div style={{ color: 'white' }}>
                      {analytics.orientationStats && Object.entries(analytics.orientationStats).map(([orientation, count]) => (
                        <p key={orientation}>
                          {orientation.charAt(0).toUpperCase() + orientation.slice(1)}: <strong>{count}</strong>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}> ({((count / analytics.totalUsers) * 100).toFixed(1)}%)</span>
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Looking For Preferences */}
                  <div style={{ background: 'rgba(81, 207, 102, 0.1)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: '#51cf66', margin: '0 0 15px 0' }}>💕 Looking For</h4>
                    <div style={{ color: 'white' }}>
                      {analytics.lookingForStats && Object.entries(analytics.lookingForStats).map(([preference, count]) => (
                        <p key={preference}>
                          {preference.charAt(0).toUpperCase() + preference.slice(1)}: <strong>{count}</strong>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}> ({((count / analytics.totalUsers) * 100).toFixed(1)}%)</span>
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Age Groups */}
                  <div style={{ background: 'rgba(255, 193, 7, 0.1)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: '#ffc107', margin: '0 0 15px 0' }}>📅 Age Distribution</h4>
                    <div style={{ color: 'white' }}>
                      {analytics.ageGroups && Object.entries(analytics.ageGroups).map(([ageGroup, count]) => (
                        <p key={ageGroup}>
                          {ageGroup}: <strong>{count}</strong>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}> ({((count / analytics.totalUsers) * 100).toFixed(1)}%)</span>
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Compatibility Insights */}
                  <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: 'white', margin: '0 0 15px 0' }}>🎯 Insights</h4>
                    <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                      <p>• Most popular age group: <strong>{analytics.ageGroups ? Object.entries(analytics.ageGroups).sort((a, b) => b[1] - a[1])[0]?.[0] : 'N/A'}</strong></p>
                      <p>• Dominant gender: <strong>{analytics.genderStats ? Object.entries(analytics.genderStats).sort((a, b) => b[1] - a[1])[0]?.[0] : 'N/A'}</strong></p>
                      <p>• Most common preference: <strong>{analytics.lookingForStats ? Object.entries(analytics.lookingForStats).sort((a, b) => b[1] - a[1])[0]?.[0] : 'N/A'}</strong></p>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '15px',
              padding: '25px'
            }}>
              <h3 style={{ color: '#ff6b6b', marginBottom: '20px' }}>📋 Message Reports</h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  tableLayout: 'fixed'  // ADD THIS FOR PROPER ALIGNMENT
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '15%' }}>Reporter</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '15%' }}>Reported User</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '12%' }}>Reason</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '25%' }}>Message</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '12%' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '10%' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#ff6b6b', width: '11%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsLoading ? (
                      <tr>
                        <td colSpan="7" style={{ 
                          padding: '20px', 
                          textAlign: 'center', 
                          color: 'rgba(255, 255, 255, 0.7)' 
                        }}>
                          Loading reports...
                        </td>
                      </tr>
                    ) : reports.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ 
                          padding: '20px', 
                          textAlign: 'center', 
                          color: 'rgba(255, 255, 255, 0.7)' 
                        }}>
                          No reports yet.
                        </td>
                      </tr>
                    ) : (
                      reports.map(report => (
                        <tr key={report.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <td style={{ padding: '12px', color: 'white' }}>
                            {report.reporter?.name || 'Unknown'}
                          </td>
                          <td style={{ padding: '12px', color: 'white' }}>
                            {report.reported_user?.name || 'Unknown'}
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            {report.reason}
                          </td>
                          <td style={{ 
                            padding: '12px', 
                            color: 'rgba(255, 255, 255, 0.8)',
                            wordWrap: 'break-word',
                            maxWidth: '200px'
                          }}>
                            <div style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={report.message_content}>
                              {report.message_content || 'Message not found'}
                            </div>
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            {new Date(report.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              background: report.status === 'resolved' ? 'rgba(81, 207, 102, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                              color: report.status === 'resolved' ? '#51cf66' : '#ffc107'
                            }}>
                              {report.status || 'pending'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            {report.status !== 'resolved' && (
                              <button
                                onClick={() => resolveReport(report.id)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: 'rgba(81, 207, 102, 0.2)',
                                  color: '#51cf66',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Resolve
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;